import { OWNERS_HUB_ROUTES } from '@/constants/routes';
import { ONBOARDING_STATUSES, SessionStatus } from '@/store/slices/session';

/**
 * Single source of truth for "where does an authenticated user belong, given the
 * state of their brand?". Previously this branch was duplicated (and subtly
 * divergent) across the login module, the register module, and nowhere at the
 * app root. Centralising it here lets the root guard ({@link
 * ../components/fixed-routes/root-redirect}), login, and register all agree.
 *
 * The mapping:
 *  - deploying, or a failed deploy to retry → the deploy step of `/account/onboarding`;
 *  - mid-setup (paid, customising name/region/domain/logo, not yet live) → the account home when
 *    the owner already has a live brand (its "Finish setting up" notice opens the wizard), else
 *    the deploy wizard at `/account/onboarding` (STUDIO_CONFIGURATOR);
 *  - has at least one (deployed) brand → the account home;
 *  - otherwise (new user / no brand yet) → the configurator wizard (the v0 flow, /easy-setup).
 *
 * Register used to send new users to the v2 configurator; it now funnels through
 * here, matching login.
 */
export type PostAuthRouteInputs = {
  /** `state.session.status` — null when there is no in-flight configurator session. */
  sessionStatus: SessionStatus | null;
  /** `state.casinoInstance.loaded` — the brand list has resolved (not merely empty). */
  brandsLoaded: boolean;
  /** `state.casinoInstance.brands.length`. */
  brandsCount: number;
  /**
   * Base path of the configurator flow a NEW owner (no brand yet) should start. Defaults
   * to the `v0` flow (`/easy-setup`), the one every owner is funnelled through; callers pass
   * `resolveConfiguratorFlowBase()` (src/utils/configurator-flow.ts), the single source of
   * truth for which flow. Kept an input (not resolved here) so this function stays pure/testable.
   */
  configuratorBase?: string;
};

// "Paid, setting the brand up, not yet live" → the deploy wizard (onboarding). The status
// set is defined once in the session slice ({@link ONBOARDING_STATUSES}) and shared with the
// brand switcher + configurator guard so the three can never drift apart.
export const resolvePostAuthRoute = ({
  sessionStatus,
  brandsLoaded,
  brandsCount,
  configuratorBase = OWNERS_HUB_ROUTES.CONFIGURATOR_V0,
}: PostAuthRouteInputs): string => {
  // A session whose setup is finished (deploy phase: mid-deploy, or a failed deploy to retry) must land
  // ON the deploy step, not the wizard start. Falling through to step 1 steps the owner into the name &
  // domain step, which calls the suggestion API with a session the backend no longer accepts for setup
  // (INVALID_SESSION_STATE), and the resulting reauth kicks them back to sign-in. The deploy step reads
  // the same status (deploying → live progress; failed → review, to relaunch). Both are in
  // ONBOARDING_STATUSES, so this must precede it.
  if (
    sessionStatus === SessionStatus.deployment_in_progress ||
    sessionStatus === SessionStatus.deployment_failed
  ) {
    return `${OWNERS_HUB_ROUTES.STUDIO_CONFIGURATOR}/deploy`;
  }

  // Paid and setting up (region → name & domain → logo → deploy), but not yet live. An owner who already
  // has a live brand lands on Home, whose Important Now "Finish setting up" notice opens the wizard; an
  // owner with none goes straight into the wizard, which opens where the build stands. The brand list
  // holds deployed brands only (a build gets its casino instance when its deploy completes), so a
  // non-empty one means another, live brand.
  if (sessionStatus != null && ONBOARDING_STATUSES.has(sessionStatus)) {
    return brandsLoaded && brandsCount > 0 ? OWNERS_HUB_ROUTES.STUDIO_HOME : OWNERS_HUB_ROUTES.STUDIO_CONFIGURATOR;
  }

  // Has at least one brand → the account home. Only trust an empty list once it
  // has actually loaded; a not-yet-loaded (or rejected) list must NOT be read as
  // "no brands" — that is what used to strand stale sessions on a broken page.
  if (brandsLoaded && brandsCount > 0) {
    return OWNERS_HUB_ROUTES.STUDIO_HOME;
  }

  // New user / no brand yet → start (or resume) the configurator wizard for the resolved
  // flow (always `v0` now; the full `v3` flow is parked).
  return configuratorBase;
};

export default resolvePostAuthRoute;
